/**
 * ResizableSplit — two panes with a draggable divider between them.
 *
 * Built for the Standortbaum: a fixed-width tree column truncates exactly when
 * it matters, because expanding four levels (Werk → Liegenschaft → Gebäude →
 * Montageort) both indents the row and lengthens the name. The operator needs
 * to widen the pane, not guess at an ellipsis.
 *
 * Three things it does that a CSS grid cannot:
 *
 *   - **The width persists** (`storageKey`). A width you have to re-drag on
 *     every app open is a width you stop using.
 *   - **The divider is keyboard-operable.** It is a real `separator` with
 *     arrow-key resize and Home/End for the bounds — dragging is a mouse
 *     affordance, and this control cannot be mouse-only.
 *   - **It stacks below `stackBelow`.** A split pane on a narrow window is two
 *     unusable columns; below the threshold the panes go full width and the
 *     divider disappears rather than becoming a 4px target.
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useLocale } from '../i18n';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    gap: 0,
  },
  stacked: {
    flexDirection: 'column',
  },
  left: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  right: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    flex: '0 0 auto',
    width: '10px',
    cursor: 'col-resize',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    // The grab target is 10px wide but only a 2px line shows — a hairline is
    // hard to hit, a 10px bar is visual noise.
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  dividerLine: {
    width: '2px',
    height: '100%',
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusSmall,
  },
  dragging: {
    backgroundColor: tokens.colorBrandBackground2,
  },
});

export interface ResizableSplitProps {
  left: ReactNode;
  right: ReactNode;
  /** Starting width of the left pane, in px. */
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  /** Persist the width under this key. Omit to keep it per-mount. */
  storageKey?: string;
  /** Below this container width (px) the panes stack. */
  stackBelow?: number;
  /** Accessible name for the divider — defaults to a generic label. */
  ariaLabel?: string;
}

/** Arrow-key step, in px. Shift multiplies it. */
const STEP = 24;

export function ResizableSplit({
  left,
  right,
  defaultLeftWidth = 360,
  minLeftWidth = 240,
  maxLeftWidth = 900,
  storageKey,
  stackBelow = 720,
  ariaLabel,
}: ResizableSplitProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const rootRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [stacked, setStacked] = useState(false);

  const [width, setWidth] = useState<number>(() => {
    if (!storageKey) return defaultLeftWidth;
    const stored = Number(localStorage.getItem(`split:${storageKey}`));
    return Number.isFinite(stored) && stored > 0 ? stored : defaultLeftWidth;
  });

  const clamp = useCallback(
    (px: number) => Math.min(maxLeftWidth, Math.max(minLeftWidth, px)),
    [minLeftWidth, maxLeftWidth],
  );

  const commit = useCallback((px: number) => {
    const next = clamp(px);
    setWidth(next);
    if (storageKey) localStorage.setItem(`split:${storageKey}`, String(next));
  }, [clamp, storageKey]);

  // Stack on a narrow container, not a narrow viewport: these panes live
  // inside a Workbench window that the user resizes independently of the
  // browser, so a media query would measure the wrong thing.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      setStacked(entry.contentRect.width < stackBelow);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [stackBelow]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !rootRef.current) return;
    commit(e.clientX - rootRef.current.getBoundingClientRect().left);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    setDragging(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? STEP * 4 : STEP;
    if (e.key === 'ArrowLeft') { e.preventDefault(); commit(width - step); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); commit(width + step); }
    else if (e.key === 'Home') { e.preventDefault(); commit(minLeftWidth); }
    else if (e.key === 'End') { e.preventDefault(); commit(maxLeftWidth); }
  };

  return (
    <div ref={rootRef} className={`${styles.root} ${stacked ? styles.stacked : ''}`}>
      <div
        className={styles.left}
        style={stacked ? undefined : { flex: `0 0 ${width}px`, width }}
      >
        {left}
      </div>

      {stacked ? null : (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={ariaLabel ?? t('split.resize')}
          aria-valuenow={width}
          aria-valuemin={minLeftWidth}
          aria-valuemax={maxLeftWidth}
          tabIndex={0}
          className={`${styles.divider} ${dragging ? styles.dragging : ''}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          onDoubleClick={() => commit(defaultLeftWidth)}
        >
          <div className={styles.dividerLine} />
        </div>
      )}

      <div className={styles.right}>{right}</div>
    </div>
  );
}
